/**
 * TWCalendarPage — public calendar + flyer gallery for Word of God.
 * Route: /tw
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BookOpen, Images } from 'lucide-react';
import {
  getTWEntryDatesInMonth,
  getTWEntriesWithFlyers,
  todayId,
} from '@/services/twService';
import type { TWEntry } from '@/services/twService';
import { cn } from '@/utils/cn';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function formatShortDate(dateId: string): string {
  const d = new Date(dateId + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function TWCalendarPage() {
  const navigate = useNavigate();
  const today    = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based
  const [entries,      setEntries]      = useState<Set<string>>(new Set());
  const [flyerEntries, setFlyerEntries] = useState<TWEntry[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [galleryLoading, setGalleryLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTWEntryDatesInMonth(year, month).then(s => {
      setEntries(s);
      setLoading(false);
    });
  }, [year, month]);

  useEffect(() => {
    setGalleryLoading(true);
    getTWEntriesWithFlyers().then(items => {
      setFlyerEntries(items);
      setGalleryLoading(false);
    });
  }, []);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build calendar grid
  const firstDay    = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = todayId();
  const fmt = (d: number) =>
    `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
          <BookOpen className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-base font-bold text-gray-900">Word of God</h1>
          <p className="text-xs text-gray-500">Angels Army Ministry</p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-5">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-5">
          <button onClick={prevMonth}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="text-center">
            <p className="text-base font-bold text-gray-900">{MONTH_NAMES[month - 1]} {year}</p>
            <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); }}
              className="text-xs text-amber-600 font-semibold mt-0.5">Today</button>
          </div>
          <button onClick={nextMonth}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-[10px] font-bold text-gray-400 py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className={cn('grid grid-cols-7 gap-1', loading && 'opacity-50')}>
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const id       = fmt(day);
            const isToday  = id === todayStr;
            const hasEntry = entries.has(id);
            const isFuture = id > todayStr;
            return (
              <button
                key={id}
                onClick={() => navigate(`/tw/${id}`)}
                className={cn(
                  'aspect-square flex flex-col items-center justify-center rounded-xl text-sm font-semibold transition-all relative',
                  isToday
                    ? 'bg-amber-500 text-white shadow-md'
                    : hasEntry
                    ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    : isFuture
                    ? 'text-gray-300 hover:bg-gray-50'
                    : 'text-gray-600 hover:bg-gray-100',
                )}
              >
                {day}
                {hasEntry && !isToday && (
                  <span className="absolute bottom-1 w-1 h-1 rounded-full bg-amber-400" />
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 justify-center">
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> Today
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-3 h-3 rounded-full bg-amber-100 border border-amber-300 inline-block" /> Has entry
          </span>
        </div>
      </div>

      {/* ── Flyer Gallery ─────────────────────────────────────────────────── */}
      <div className="max-w-md mx-auto px-4 pb-10">
        <div className="flex items-center gap-2 mb-4 mt-2">
          <Images className="h-4 w-4 text-amber-500" />
          <h2 className="text-sm font-bold text-gray-800">Flyer Gallery</h2>
          {!galleryLoading && flyerEntries.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">{flyerEntries.length} flyers</span>
          )}
        </div>

        {galleryLoading ? (
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-gray-100 animate-pulse" style={{ aspectRatio: '9/16' }} />
            ))}
          </div>
        ) : flyerEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Images className="h-10 w-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">No flyers yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {flyerEntries.map(e => (
              <button
                key={e.date}
                onClick={() => navigate(`/tw/${e.date}`)}
                className="group relative overflow-hidden rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-amber-400"
                style={{ aspectRatio: '9/16' }}
                title={formatShortDate(e.date)}
              >
                <img
                  src={e.flyerUrl!}
                  alt={e.date}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Date badge on hover */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-2 translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                  <p className="text-white text-[10px] font-semibold text-center">
                    {formatShortDate(e.date)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
